import type React from 'react';
import { useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Icon, IconButton, Menu, Text } from '@zillow/constellation-10';
import { IconDeleteOutline, IconEditOutline, IconMoreFilled } from '@zillow/constellation-icons';
import { PartialMessage } from '@zg-rentals/backend-styled-components';

import CostsandFeesDeleteFeeModal from '../../../Modal/CostsandFeesDeleteFeeModal';

import { useSelector } from '../../../../selectors/useSelector';

import CostsAndFeesSFCSEvents from '../../../../model/ClickstreamEvent/CostsAndFeesSFCSEvents';

import clickstreamUtils from '../../../../utils/clickstreamUtils';

import { TRIGGER_LOCATIONS_MAP } from '../constants';

import type {
  GetSingleFamilyFeesDictionaryItemsDto,
  PartialMessageTextAndUrlDto,
  SingleFamilyFeeDto,
} from '@zg-rentals/ts-rental-manager-api-client';

interface FeeListItemProps {
  fee: SingleFamilyFeeDto;
  dictionaryItems: GetSingleFamilyFeesDictionaryItemsDto;
  handleEditFeeClick: (fee: SingleFamilyFeeDto) => void;
  displayedLocation: string;
}

type FeeDisplayObject = {
  feeName: string;
  feeRequirementType: string;
  feeRefundableType: string;
  feeAmountDisplay: React.ReactNode;
};

const getFeeAmountDisplay = (partialMessages: Array<PartialMessageTextAndUrlDto>, textStyle: 'body' | 'body-sm') => {
  return (
    <Text asChild>
      <span>
        <PartialMessage fontType={textStyle === 'body' ? 'body' : 'bodySmall'} partialMessages={partialMessages} />
      </span>
    </Text>
  );
};

const getFeeNameDisplay = (fee: SingleFamilyFeeDto, dictionaryItems: GetSingleFamilyFeesDictionaryItemsDto) => {
  if (Object.keys(dictionaryItems?.mainCategories).includes(fee.feeCategory)) {
    return dictionaryItems?.mainCategories[fee?.feeCategory]?.feeType[fee?.feeType]?.displayName;
  } else {
    return dictionaryItems?.otherCategories.categories[fee?.feeCategory]?.feeType[fee?.feeType]?.displayName;
  }
};

export const FeeListItem: React.FC<FeeListItemProps> = ({
  fee,
  dictionaryItems,
  handleEditFeeClick,
  displayedLocation,
}) => {
  const { currentProperty, user } = useSelector((state) => {
    return {
      currentProperty: state.properties.currentProperty,
      user: state.user,
    };
  }, shallowEqual);

  const { active, dateLastActivatedTimestamp } = currentProperty;

  const [isDeleteFeeModalOpen, setIsDeleteFeeModalOpen] = useState(false);

  const textStyle = displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? 'body' : 'body-sm';

  const triggerLocation = useMemo(() => {
    const mappedLocation = TRIGGER_LOCATIONS_MAP[displayedLocation as keyof typeof TRIGGER_LOCATIONS_MAP];
    const listingStatusSuffix = clickstreamUtils.getListingStatusSuffix(active, dateLastActivatedTimestamp);

    return `${mappedLocation}${listingStatusSuffix}`;
  }, [active, dateLastActivatedTimestamp, displayedLocation]);

  const displayObject: FeeDisplayObject = {
    feeName: getFeeNameDisplay(fee, dictionaryItems),
    feeRequirementType: dictionaryItems?.feeRequirementType[fee?.feeRequirementType]?.displayName,
    feeRefundableType: dictionaryItems?.feeRefundableType[fee?.feeRefundableType]?.displayName,
    feeAmountDisplay: getFeeAmountDisplay(fee?.styledComponents?.feeCalculationAndFrequencyDisplay, textStyle),
  };
  const handleDeleteClick = () => {
    // open the delete fee modal here and pass in the fee object
    setIsDeleteFeeModalOpen(true);
    CostsAndFeesSFCSEvents.ClickDeleteFee({ currentProperty, user, triggerLocation });
  };

  return (
    <>
      <Box
        css={{
          marginBlock: '400',
          marginLeft: displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? '200' : 'none',
        }}
      >
        <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Box css={{ marginBottom: '200' }}>
            <Text textStyle={textStyle === 'body' ? 'body-bold' : 'body-sm-bold'}>{displayObject.feeName}</Text>
            <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
              <Text textStyle={textStyle}>{displayObject.feeAmountDisplay}</Text>
              <Text textStyle={textStyle} css={{ paddingInline: '200' }}>
                |
              </Text>
              <Text textStyle={textStyle}>{displayObject.feeRequirementType}</Text>
              {fee?.feeRefundableType && (
                <>
                  <Text textStyle={textStyle} css={{ paddingInline: '200' }}>
                    |
                  </Text>
                  <Text textStyle={textStyle}>{displayObject.feeRefundableType}</Text>
                </>
              )}
            </Box>
          </Box>
          <div>
            <Menu
              trigger={
                <IconButton
                  tone="neutral"
                  emphasis="bare"
                  data-testid="FeeListItem-MenuTrigger"
                  title="Fee options"
                  icon={<IconMoreFilled />}
                />
              }
              content={
                <>
                  <Menu.Item
                    onClick={() => {
                      handleEditFeeClick(fee);
                    }}
                    data-eventid={CostsAndFeesSFCSEvents.ClickEditFee.data_event_id}
                  >
                    <Menu.ItemIcon>
                      <Icon render={<IconEditOutline />} />
                    </Menu.ItemIcon>
                    <Menu.ItemLabel>Edit</Menu.ItemLabel>
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      handleDeleteClick();
                    }}
                    data-testid="FeeListItem-DeleteMenuItem"
                    data-eventid={CostsAndFeesSFCSEvents.ClickDeleteFee.data_event_id}
                  >
                    <Menu.ItemIcon>
                      <Icon render={<IconDeleteOutline />} />
                    </Menu.ItemIcon>
                    <Menu.ItemLabel>Delete</Menu.ItemLabel>
                  </Menu.Item>
                </>
              }
            />
          </div>
        </Box>
      </Box>

      {isDeleteFeeModalOpen && (
        <CostsandFeesDeleteFeeModal
          feeId={fee.costAndFeeId}
          setShowDeleteFeeModal={setIsDeleteFeeModalOpen}
          isSingleFamilyFee={true}
        />
      )}
    </>
  );
};
